// Meeting minutes + attendance (NCUA-style) and "Log past meeting" modal.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { CheckCircle2, FileDown, Lock, NotebookPen, Plus, Trash2, X } from "lucide-react";
import { Field, FORMAT_LABELS, Overlay, downloadPdf, formatIso, inputCls } from "./boardShared";

const errMsg = (e) => formatApiError(e.response?.data?.detail) || "Something went wrong";

export function computeQuorum(attendance = []) {
  const directors = attendance.filter((a) => a.group === "board");
  const pool = directors.length ? directors : attendance;
  const base = pool.length;
  const required = base ? Math.floor(base / 2) + 1 : 0;
  const present = pool.filter((a) => a.status === "present").length;
  return { basis: directors.length ? "directors" : "members", base, required, present, met: base > 0 && present >= required };
}

function FormatSelect({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
      {Object.entries(FORMAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Log past meeting
// ---------------------------------------------------------------------------

export function LogMeetingModal({ members, onClose, onSaved }) {
  const nowLocal = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }, []);
  const [form, setForm] = useState({
    title: "", description: "", starts_at: "", duration_minutes: 60,
    meeting_format: "in_person", location: "", attendee_ids: members.map((m) => m.id),
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setForm((f) => ({
    ...f, attendee_ids: f.attendee_ids.includes(id) ? f.attendee_ids.filter((x) => x !== id) : [...f.attendee_ids, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/board/meetings/log", {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 60,
      });
      toast.success("Meeting logged — now record the minutes");
      onSaved(r.data.meeting);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} testId="admin-board-log-meeting-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="relative max-w-2xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream"><X size={18} /></button>
        <div className="label-eyebrow flex items-center gap-2"><NotebookPen size={12} className="text-bdu-gold" /> Log Past Meeting</div>
        <h3 className="mt-3 font-serif text-2xl text-bdu-cream">Record a meeting held outside the app</h3>
        <p className="mt-1 text-xs text-bdu-cream/55">No Jitsi room is created and no invites are sent.</p>

        <div className="mt-5 space-y-4">
          <Field label="Title *">
            <input required minLength={2} value={form.title} onChange={upd("title")} className={inputCls}
                   placeholder="Organizing Committee Meeting #3" data-testid="admin-board-log-title" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Held on (your local time) *">
              <input type="datetime-local" required max={nowLocal} value={form.starts_at} onChange={upd("starts_at")}
                     className={inputCls} data-testid="admin-board-log-starts" />
            </Field>
            <Field label="Duration (minutes)">
              <input type="number" min={5} max={600} value={form.duration_minutes} onChange={upd("duration_minutes")} className={inputCls} />
            </Field>
            <Field label="Format">
              <FormatSelect value={form.meeting_format} onChange={(v) => setForm((f) => ({ ...f, meeting_format: v }))}
                            testId="admin-board-log-format" />
            </Field>
            <Field label="Location / dial-in">
              <input value={form.location} onChange={upd("location")} className={inputCls}
                     placeholder="Church fellowship hall, Atlanta" data-testid="admin-board-log-location" />
            </Field>
          </div>
          <Field label="Short description">
            <input value={form.description} onChange={upd("description")} className={inputCls} maxLength={200} />
          </Field>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-cream/60 mb-2">Invited / expected</div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-bdu-gold/15 p-3"
                 data-testid="admin-board-log-attendees">
              {members.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm text-bdu-cream/80 cursor-pointer">
                  <input type="checkbox" checked={form.attendee_ids.includes(m.id)} onChange={() => toggle(m.id)} />
                  <span className="truncate">{m.name} <span className="text-bdu-cream/40">· {m.group === "board" ? "Board" : "Committee"}</span></span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-bdu-cream/45">All active directors are always added to attendance for the quorum count.</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="btn-gold text-xs disabled:opacity-40" data-testid="admin-board-log-submit">
            {busy ? "Saving…" : "Log meeting & record minutes"}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Minutes editor
// ---------------------------------------------------------------------------

const PAYLOAD_KEYS = ["meeting_format", "location", "presiding_officer", "recording_secretary",
  "called_to_order_at", "adjourned_at", "attendance", "guests", "motions", "action_items", "notes"];

function toPayload(form) {
  const p = Object.fromEntries(PAYLOAD_KEYS.map((k) => [k, form[k]]));
  p.motions = (form.motions || []).filter((m) => m.text.trim()).map((m) => ({
    ...m, votes_for: Number(m.votes_for) || 0, votes_against: Number(m.votes_against) || 0, abstentions: Number(m.abstentions) || 0,
  }));
  p.action_items = (form.action_items || []).filter((a) => a.task.trim());
  return p;
}

function Section({ n, title, children, right }) {
  return (
    <div className="border-t border-bdu-gold/15 pt-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[11px] tracking-[0.22em] uppercase text-bdu-gold">{n}. {title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function QuorumBadge({ q }) {
  return (
    <span data-testid="minutes-quorum-badge"
          className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${q.met ? "border-emerald-400/40 text-emerald-300" : "border-red-400/40 text-red-300"}`}>
      Quorum {q.met ? "met" : "not met"} · {q.present}/{q.base} {q.basis} (need {q.required})
    </span>
  );
}

function DetailsFields({ form, set }) {
  const f = (k) => ({ value: form[k] || "", onChange: (e) => set(k, e.target.value) });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Format"><FormatSelect value={form.meeting_format} onChange={(v) => set("meeting_format", v)} testId="minutes-format" /></Field>
      <Field label="Location / link"><input {...f("location")} className={inputCls} data-testid="minutes-location" /></Field>
      <Field label="Presiding officer *"><input {...f("presiding_officer")} list="minutes-names" className={inputCls} placeholder="Chair's name" data-testid="minutes-presiding" /></Field>
      <Field label="Recording secretary *"><input {...f("recording_secretary")} list="minutes-names" className={inputCls} data-testid="minutes-recorder" /></Field>
      <Field label="Called to order at *"><input type="time" {...f("called_to_order_at")} className={inputCls} data-testid="minutes-called-at" /></Field>
      <Field label="Adjourned at *"><input type="time" {...f("adjourned_at")} className={inputCls} data-testid="minutes-adjourned-at" /></Field>
    </div>
  );
}

const STATUSES = [["present", "Present"], ["absent", "Absent"], ["excused", "Excused"]];

function AttendanceEditor({ form, set, members }) {
  const rows = form.attendance || [];
  const missing = members.filter((m) => !rows.some((r) => r.member_id === m.id));
  const setStatus = (i, status) => set("attendance", rows.map((r, j) => (j === i ? { ...r, status } : r)));
  const add = (id) => {
    const m = members.find((x) => x.id === id);
    if (m) set("attendance", [...rows, { member_id: m.id, name: m.name, title: m.title || "", group: m.group, status: "present" }]);
  };
  return (
    <div className="space-y-2" data-testid="minutes-attendance">
      {rows.map((r, i) => (
        <div key={r.member_id} className="flex items-center justify-between gap-3 flex-wrap border border-bdu-gold/10 px-3 py-2" data-testid={`minutes-att-row-${r.member_id}`}>
          <div className="min-w-0">
            <div className="text-sm text-bdu-cream truncate">{r.name}</div>
            <div className="text-[11px] text-bdu-cream/45">{r.title || "—"} · {r.group === "board" ? "Director" : "Committee"}</div>
          </div>
          <div className="flex">
            {STATUSES.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setStatus(i, k)} data-testid={`minutes-att-${r.member_id}-${k}`}
                      className={`text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border -ml-px transition-colors ${r.status === k ? "bg-bdu-gold text-bdu-bg border-bdu-gold" : "border-bdu-gold/25 text-bdu-cream/60 hover:text-bdu-cream"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="text-xs italic text-bdu-cream/45">No roster members on this meeting yet.</div>}
      {missing.length > 0 && (
        <select value="" onChange={(e) => add(e.target.value)} className={`${inputCls} mt-2`} data-testid="minutes-att-add">
          <option value="">+ Add someone from the roster…</option>
          {missing.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.group === "board" ? "Director" : "Committee"}</option>)}
        </select>
      )}
      <Field label="Guests / others present" className="pt-2">
        <input value={form.guests || ""} onChange={(e) => set("guests", e.target.value)} className={inputCls}
               placeholder="e.g. NCUA field examiner, sponsor reps" data-testid="minutes-guests" />
      </Field>
    </div>
  );
}

const EMPTY_MOTION = { text: "", moved_by: "", seconded_by: "", votes_for: 0, votes_against: 0, abstentions: 0, result: "passed" };

function MotionsEditor({ form, set }) {
  const rows = form.motions || [];
  const upd = (i, patch) => set("motions", rows.map((m, j) => {
    if (j !== i) return m;
    const next = { ...m, ...patch };
    if (("votes_for" in patch || "votes_against" in patch) && ["passed", "failed"].includes(next.result)) {
      next.result = Number(next.votes_for) > Number(next.votes_against) ? "passed" : "failed";
    }
    return next;
  }));
  const num = (i, k) => ({ type: "number", min: 0, value: rows[i][k], onChange: (e) => upd(i, { [k]: e.target.value }), className: inputCls });
  return (
    <div className="space-y-4" data-testid="minutes-motions">
      {rows.length === 0 && <div className="text-xs italic text-bdu-cream/45">No motions yet.</div>}
      {rows.map((m, i) => (
        <div key={i} className="border border-bdu-gold/15 p-4 space-y-3" data-testid={`minutes-motion-${i}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-cream/60">Motion {i + 1}</div>
            <button type="button" onClick={() => set("motions", rows.filter((_, j) => j !== i))}
                    className="text-red-300/70 hover:text-red-300" data-testid={`minutes-motion-${i}-remove`}><Trash2 size={14} /></button>
          </div>
          <textarea rows={2} value={m.text} onChange={(e) => upd(i, { text: e.target.value })} className={`${inputCls} resize-none`}
                    placeholder="Resolved, that…" data-testid={`minutes-motion-${i}-text`} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Moved by"><input list="minutes-names" value={m.moved_by} onChange={(e) => upd(i, { moved_by: e.target.value })} className={inputCls} data-testid={`minutes-motion-${i}-moved`} /></Field>
            <Field label="Seconded by"><input list="minutes-names" value={m.seconded_by} onChange={(e) => upd(i, { seconded_by: e.target.value })} className={inputCls} data-testid={`minutes-motion-${i}-seconded`} /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="For"><input {...num(i, "votes_for")} data-testid={`minutes-motion-${i}-for`} /></Field>
            <Field label="Against"><input {...num(i, "votes_against")} data-testid={`minutes-motion-${i}-against`} /></Field>
            <Field label="Abstain"><input {...num(i, "abstentions")} data-testid={`minutes-motion-${i}-abstain`} /></Field>
            <Field label="Result">
              <select value={m.result} onChange={(e) => upd(i, { result: e.target.value })} className={inputCls} data-testid={`minutes-motion-${i}-result`}>
                <option value="passed">Passed</option><option value="failed">Failed</option>
                <option value="tabled">Tabled</option><option value="withdrawn">Withdrawn</option>
              </select>
            </Field>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => set("motions", [...rows, { ...EMPTY_MOTION }])}
              className="text-xs tracking-[0.2em] uppercase text-bdu-gold inline-flex items-center gap-1" data-testid="minutes-add-motion">
        <Plus size={12} /> Add motion
      </button>
    </div>
  );
}

function ActionItemsEditor({ form, set }) {
  const rows = form.action_items || [];
  const upd = (i, patch) => set("action_items", rows.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  return (
    <div className="space-y-2" data-testid="minutes-actions">
      {rows.map((a, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`minutes-action-${i}`}>
          <input value={a.task} onChange={(e) => upd(i, { task: e.target.value })} placeholder="Task" className={`${inputCls} col-span-12 sm:col-span-6`} data-testid={`minutes-action-${i}-task`} />
          <input list="minutes-names" value={a.owner} onChange={(e) => upd(i, { owner: e.target.value })} placeholder="Owner" className={`${inputCls} col-span-6 sm:col-span-3`} data-testid={`minutes-action-${i}-owner`} />
          <input type="date" value={a.due_date} onChange={(e) => upd(i, { due_date: e.target.value })} className={`${inputCls} col-span-5 sm:col-span-2`} data-testid={`minutes-action-${i}-due`} />
          <button type="button" onClick={() => set("action_items", rows.filter((_, j) => j !== i))} className="col-span-1 text-red-300/70 hover:text-red-300"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => set("action_items", [...rows, { task: "", owner: "", due_date: "" }])}
              className="text-xs tracking-[0.2em] uppercase text-bdu-gold inline-flex items-center gap-1" data-testid="minutes-add-action">
        <Plus size={12} /> Add action item
      </button>
    </div>
  );
}

function ApprovedBanner({ m }) {
  return (
    <div className="border border-emerald-400/30 bg-emerald-400/5 p-4 text-sm text-bdu-cream/80" data-testid="minutes-approved-banner">
      <div className="flex items-center gap-2 text-emerald-300 text-[11px] tracking-[0.22em] uppercase"><Lock size={12} /> Approved &amp; locked</div>
      <p className="mt-2">Approved by <strong>{m.approved_by_name}</strong>, Secretary, on {formatIso(m.approved_at)}.</p>
      <p className="mt-1 text-[11px] text-bdu-cream/45 break-all">SHA-256 {m.content_hash}</p>
    </div>
  );
}

function ApproveBar({ busy, name, setName, onConfirm, onCancel }) {
  return (
    <div className="border border-bdu-gold/40 bg-bdu-gold/5 p-4 space-y-3" data-testid="minutes-approve-panel">
      <p className="text-xs text-bdu-cream/75">Approving locks these minutes permanently. Corrections then have to be made by a motion at a later meeting.</p>
      <Field label="Secretary's full name (signs the certification) *">
        <input value={name} onChange={(e) => setName(e.target.value)} list="minutes-names" className={inputCls} data-testid="minutes-approve-name" />
      </Field>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">Back</button>
        <button type="button" disabled={busy || name.trim().length < 2} onClick={onConfirm}
                className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-40" data-testid="minutes-approve-confirm">
          <Lock size={12} /> {busy ? "Approving…" : "Approve & lock"}
        </button>
      </div>
    </div>
  );
}

export function MinutesModal({ meetingId, members, onClose, onChanged }) {
  const [meeting, setMeeting] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [approving, setApproving] = useState(false);
  const [approveName, setApproveName] = useState("");

  useEffect(() => {
    api.get(`/admin/board/meetings/${meetingId}/minutes`)
      .then((r) => { setMeeting(r.data.meeting); setForm(r.data.minutes); setApproveName(r.data.minutes.recording_secretary || ""); })
      .catch((e) => { toast.error(errMsg(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const quorum = useMemo(() => computeQuorum(form?.attendance), [form?.attendance]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const locked = form?.status === "approved";
  const names = useMemo(() => [...new Set([...(form?.attendance || []).map((a) => a.name), ...members.map((m) => m.name)])], [form?.attendance, members]);

  const save = async (quiet = false) => {
    setBusy("save");
    try {
      const r = await api.put(`/admin/board/meetings/${meetingId}/minutes`, toPayload(form));
      setForm(r.data.minutes);
      if (!quiet) toast.success("Draft saved");
      onChanged();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
    finally { setBusy(""); }
  };

  const approve = async () => {
    if (!(await save(true))) return;
    setBusy("approve");
    try {
      const r = await api.post(`/admin/board/meetings/${meetingId}/minutes/approve`, { secretary_name: approveName });
      setForm(r.data.minutes);
      setApproving(false);
      toast.success("Minutes approved & locked");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const pdf = async () => {
    if (!locked && !(await save(true))) return;
    setBusy("pdf");
    await downloadPdf(`/admin/board/meetings/${meetingId}/minutes.pdf`, "TDSPFCU_Minutes.pdf");
    setBusy("");
  };

  return (
    <Overlay onClose={onClose} testId="admin-board-minutes-modal">
      <div onClick={(e) => e.stopPropagation()} className="relative max-w-3xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream" data-testid="minutes-close"><X size={18} /></button>
        {!form ? <div className="text-sm text-bdu-cream/50">Loading minutes…</div> : (
          <>
            <div className="label-eyebrow flex items-center gap-2"><NotebookPen size={12} className="text-bdu-gold" /> Meeting Minutes</div>
            <h3 className="mt-3 font-serif text-2xl text-bdu-cream" data-testid="minutes-meeting-title">{meeting.title}</h3>
            <div className="mt-1 text-xs text-bdu-cream/55">{formatIso(meeting.starts_at)}{meeting.logged ? " · logged meeting" : ""}</div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span data-testid="minutes-status-badge"
                    className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${locked ? "border-emerald-400/40 text-emerald-300" : "border-bdu-gold/40 text-bdu-gold"}`}>
                {locked ? "Approved" : form.saved ? "Draft" : "Not started"}
              </span>
              <QuorumBadge q={quorum} />
            </div>

            <datalist id="minutes-names">{names.map((n) => <option key={n} value={n} />)}</datalist>

            <fieldset disabled={locked} className="mt-6 space-y-6 disabled:opacity-90">
              <Section n={1} title="Call to order & details"><DetailsFields form={form} set={set} /></Section>
              <Section n={2} title="Attendance & quorum"
                       right={!locked && <button type="button" className="text-[10px] tracking-[0.2em] uppercase text-bdu-gold" data-testid="minutes-all-present"
                                                  onClick={() => set("attendance", form.attendance.map((a) => ({ ...a, status: "present" })))}>Mark all present</button>}>
                <AttendanceEditor form={form} set={set} members={members} />
              </Section>
              <Section n={3} title="Motions & votes"><MotionsEditor form={form} set={set} /></Section>
              <Section n={4} title="Action items"><ActionItemsEditor form={form} set={set} /></Section>
              <Section n={5} title="Discussion notes">
                <textarea rows={5} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
                          className={`${inputCls} resize-y`} placeholder="Summary of reports and discussion…" data-testid="minutes-notes" />
              </Section>
            </fieldset>

            <div className="mt-6 space-y-4">
              {locked && <ApprovedBanner m={form} />}
              {approving && !locked && (
                <ApproveBar busy={busy === "approve"} name={approveName} setName={setApproveName}
                            onConfirm={approve} onCancel={() => setApproving(false)} />
              )}
              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={pdf} disabled={!!busy}
                        className="btn-ghost text-xs inline-flex items-center gap-2 disabled:opacity-40" data-testid="minutes-download-pdf">
                  <FileDown size={14} /> {busy === "pdf" ? "Preparing…" : locked ? "Download signed PDF" : "Download draft PDF"}
                </button>
                {!locked && (
                  <>
                    <button type="button" onClick={() => save()} disabled={!!busy}
                            className="btn-ghost text-xs disabled:opacity-40" data-testid="minutes-save">
                      {busy === "save" ? "Saving…" : "Save draft"}
                    </button>
                    {!approving && (
                      <button type="button" onClick={() => setApproving(true)} disabled={!!busy}
                              className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-40" data-testid="minutes-approve-open">
                        <CheckCircle2 size={14} /> Approve minutes
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Overlay>
  );
}
